import { useState, useEffect, useMemo } from "react"

import { TopLeadBreakdownData } from "../../types"
import { TopLeadBreakdownTabs } from "../ui/leads/charts/top-lead-breakdown"

export interface TransformTopLeadBreakdown extends TopLeadBreakdownData {
  netPercentage?: string
  isPositive?: boolean
}

export const useTransformTopLeadBreakdown = (
  tab: string,
  breakdownData: TopLeadBreakdownData[] | undefined,
  previousBreakdownData: TopLeadBreakdownData[] | undefined
): TransformTopLeadBreakdown[] => {
  const [currentData, setCurrentData] = useState<TransformTopLeadBreakdown[]>(
    []
  )
  const containsData = useMemo(
    () => breakdownData && breakdownData.length > 0,
    [breakdownData]
  )
  const containsPreviousData = useMemo(
    () => previousBreakdownData && previousBreakdownData.length > 0,
    [previousBreakdownData]
  )

  useEffect(() => {
    let transformedData: TransformTopLeadBreakdown[] = []
    if (containsData && containsPreviousData) {
      transformedData = breakdownData!.map(current => {
        // `previousBreakdownData` covers a 2x window (prior + current combined),
        // so prior-period count = combined - current.
        const combined =
          tab === TopLeadBreakdownTabs.URL
            ? previousBreakdownData!.find(prev => prev.url === current.url)
            : previousBreakdownData!.find(prev => prev.name === current.name)
        const priorCount = combined
          ? Math.max(combined.lead_count - current.lead_count, 0)
          : 0
        const netCount = current.lead_count - priorCount
        const netPercentage =
          priorCount > 0
            ? `${parseFloat(((netCount / priorCount) * 100).toFixed(1))}%`
            : "+100%"

        return {
          ...current,
          netPercentage,
          isPositive: netCount >= 0
        }
      })
    } else if (containsData) {
      transformedData = breakdownData!
    }
    setCurrentData(transformedData)
  }, [
    breakdownData,
    containsData,
    containsPreviousData,
    previousBreakdownData,
    tab
  ])

  return currentData
}
